/**
 * Preventive Care Service
 *
 * Calculates patient-specific preventive services that are due based on:
 * - Patient demographics (age, gender)
 * - Medical history (chronic conditions)
 * - Immunization history (time since last vaccination)
 * - Risk factors (pregnancy, immunosuppression, lifestyle factors)
 */

import { Patient } from '../types/patient'
import { Immunisation } from '../types/immunisation'
import {
  PreventiveService,
  PreventiveServiceStatus,
  PreventiveServicePriority,
  RiskFactor,
  CalculatePreventiveServicesInput,
  PreventiveServicesResult,
} from '../types/preventiveCare'

/**
 * Calculate age at a specific date (for testing and consistency)
 * This ensures we use the currentDate parameter instead of always using new Date()
 *
 * Handles ISO date format (YYYY-MM-DD) which is the standard format used in the system
 */
function calculateAgeAtDate(dateOfBirth: string, atDate: Date): number {
  if (!dateOfBirth) {
    console.warn('Missing date of birth for age calculation')
    return 0
  }

  let birthDate: Date
  if (dateOfBirth.includes('T')) {
    birthDate = new Date(dateOfBirth)
  } else if (dateOfBirth.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateOfBirth.split('-').map(Number)
    birthDate = new Date(year, month - 1, day)
  } else {
    birthDate = new Date(dateOfBirth)
  }

  if (isNaN(birthDate.getTime())) {
    console.warn('Invalid date of birth:', dateOfBirth)
    return 0
  }

  if (isNaN(atDate.getTime())) {
    console.warn('Invalid current date for age calculation, using today')
    atDate = new Date()
  }

  let age = atDate.getFullYear() - birthDate.getFullYear()
  const monthDiff = atDate.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && atDate.getDate() < birthDate.getDate())) {
    age--
  }

  if (age < 0 || age > 150) {
    console.warn('Age calculation seems incorrect:', {
      dateOfBirth,
      birthDate: birthDate.toISOString(),
      atDate: atDate.toISOString(),
      calculatedAge: age,
    })
  }

  return age
}

function hasPatientDateOfBirth(patient: Patient): boolean {
  return typeof patient.dateOfBirth === 'string' && patient.dateOfBirth.trim().length > 0
}

/** Parse YYYY-MM-DD at local noon to avoid DST edge cases */
function parseIsoDateLocal(isoDate: string): Date | null {
  const m = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0)
  return isNaN(d.getTime()) ? null : d
}

/**
 * Calculate all preventive services for a patient
 */
export function calculatePreventiveServices(
  input: CalculatePreventiveServicesInput
): PreventiveServicesResult {
  const {
    patient,
    immunisations = [],
    currentDate = new Date(),
    hasActivePregnancy = false,
  } = input

  const services: PreventiveService[] = []

  const dob = hasPatientDateOfBirth(patient)
  const age = dob ? calculateAgeAtDate(patient.dateOfBirth, currentDate) : null

  if (
    process.env.NODE_ENV === 'development' &&
    age !== null &&
    (age < 0 || age > 150)
  ) {
    console.warn('Invalid age calculated:', {
      patientId: patient.id,
      dateOfBirth: patient.dateOfBirth,
      calculatedAge: age,
      currentDate: currentDate.toISOString(),
    })
  }

  const chronicConditions = patient.chronicConditions || []

  const smokingService = calculateSmokingStatusService(patient, currentDate)
  if (smokingService) {
    services.push(smokingService)
  }

  if (age !== null) {
    services.push(
      ...calculatePneumococcalVaccineServices(
        patient,
        age,
        chronicConditions,
        immunisations,
        currentDate
      )
    )
    services.push(
      ...calculateHpvImmunisationServices(patient, age, immunisations)
    )
    services.push(...calculateCervicalScreeningServices(patient, age, currentDate))
  }

  services.push(
    ...calculateInfluenzaVaccineServices(
      patient,
      age,
      chronicConditions,
      immunisations,
      currentDate,
      hasActivePregnancy
    )
  )

  const dueServices = services.filter(
    (s) => s.status === 'due' || s.status === 'overdue'
  )
  const overdueServices = services.filter((s) => s.status === 'overdue')

  return {
    services,
    dueServices,
    overdueServices,
    dueCount: dueServices.length,
    overdueCount: overdueServices.length,
    totalCount: services.length,
  }
}

/**
 * Calculate smoking status preventive service
 */
function calculateSmokingStatusService(
  patient: Patient,
  currentDate: Date
): PreventiveService | null {
  const oneYearAgo = new Date(
    currentDate.getFullYear() - 1,
    currentDate.getMonth(),
    currentDate.getDate()
  )

  const isMissing = !patient.smokingStatus && !patient.smokingDetails

  let isOutdated = false
  let lastRecordedDate: Date | null = null

  if (patient.smokingStatusLastRecorded) {
    lastRecordedDate = new Date(patient.smokingStatusLastRecorded)
    isOutdated = lastRecordedDate < oneYearAgo
  } else if (patient.smokingDetails) {
    isOutdated = true
    lastRecordedDate = null
  }

  if (!isMissing && !isOutdated) {
    return null
  }

  const riskFactors: RiskFactor[] = []

  const status: PreventiveServiceStatus = isMissing ? 'overdue' : 'overdue'
  const reason = isMissing
    ? 'Smoking status has not been recorded'
    : lastRecordedDate
      ? `Smoking status was last recorded ${formatRelativeDate(lastRecordedDate)}`
      : 'Smoking status needs to be updated with a recorded date'

  return {
    id: `smoking-status-${patient.id}`,
    type: 'assessment',
    name: 'Smoking Status Assessment',
    description:
      'Healthcare professionals should ask and record smoking status at every visit, including type, amount, and history.',
    status,
    priority: 'high',
    reason,
    riskFactors,
    actionUrl: `/patients/${patient.id}/edit#clinical-and-lifestyle`,
    actionLabel: 'Update Smoking Status',
    metadata: {
      lastRecordedDate: lastRecordedDate?.toISOString(),
    },
  }
}

/**
 * Calculate pneumococcal vaccine recommendations
 */
function calculatePneumococcalVaccineServices(
  patient: Patient,
  age: number,
  chronicConditions: string[],
  immunisations: Immunisation[],
  currentDate: Date
): PreventiveService[] {
  const services: PreventiveService[] = []
  const PNEUMOCOCCAL_VACCINE_TYPES = [
    'pneumococcal',
    'PCV13',
    'PCV23',
    'PPV23',
    'Prevenar',
    'Pneumovax',
    'pneumococcal 23-valent',
    'pneumococcal conjugate',
    'pneumococcal polysaccharide',
  ]
  const BOOSTER_INTERVAL_DAYS = 5 * 365

  const pneumococcalImmunisations = immunisations.filter((imm) =>
    PNEUMOCOCCAL_VACCINE_TYPES.some((type) =>
      imm.type.toLowerCase().includes(type.toLowerCase())
    )
  )

  const mostRecentPneumococcal =
    pneumococcalImmunisations.length > 0
      ? pneumococcalImmunisations.reduce((latest, current) => {
          return new Date(current.date) > new Date(latest.date) ? current : latest
        })
      : null

  const daysSinceLastVaccination = mostRecentPneumococcal
    ? Math.floor(
        (currentDate.getTime() - new Date(mostRecentPneumococcal.date).getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : Infinity

  const riskFactors: RiskFactor[] = []
  let isRecommended = false
  let reason = ''
  let priority: PreventiveServicePriority = 'medium'

  if (age >= 65) {
    isRecommended = true
    riskFactors.push({ type: 'age', value: '65+', description: 'Age 65 years or older' })
    priority = 'high'

    const hasHighRiskConditions = hasHighRiskChronicConditions(chronicConditions)
    if (hasHighRiskConditions) {
      riskFactors.push({
        type: 'chronic-condition',
        value: 'high-risk',
        description: 'Chronic conditions that increase risk',
      })
      reason = 'Age 65+ with chronic conditions'
    } else {
      reason = 'Age 65+'
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('Pneumococcal recommendation for age 65+:', {
        patientId: patient.id,
        dateOfBirth: patient.dateOfBirth,
        calculatedAge: age,
      })
    }
  } else if (age < 65) {
    const hasHighRiskConditions = hasHighRiskChronicConditions(chronicConditions)
    if (hasHighRiskConditions) {
      isRecommended = true
      riskFactors.push({
        type: 'chronic-condition',
        value: 'high-risk',
        description: 'Chronic conditions that increase risk',
      })
      reason = 'Chronic conditions (under 65)'
      priority = 'high'
    }
  }

  if (isRecommended) {
    const isDue =
      !mostRecentPneumococcal || daysSinceLastVaccination >= BOOSTER_INTERVAL_DAYS

    if (isDue) {
      const dueDate = mostRecentPneumococcal
        ? new Date(
            new Date(mostRecentPneumococcal.date).getTime() +
              BOOSTER_INTERVAL_DAYS * 24 * 60 * 60 * 1000
          )
        : currentDate

      const status: PreventiveServiceStatus =
        dueDate < currentDate ? 'overdue' : 'due'

      services.push({
        id: `pneumococcal-${patient.id}`,
        type: 'immunization',
        name: 'Pneumococcal Vaccine',
        description:
          'Pneumococcal vaccination is recommended every 5 years for adults 65 years and older, or for adults with certain chronic conditions.',
        status,
        priority,
        dueDate: dueDate.toISOString().split('T')[0],
        overdueDate:
          status === 'overdue' ? dueDate.toISOString().split('T')[0] : undefined,
        reason,
        riskFactors,
        actionUrl: `#immunisations`,
        actionLabel: 'Record Immunization',
        metadata: {
          vaccineType: 'pneumococcal',
          daysSinceLastVaccination: mostRecentPneumococcal
            ? daysSinceLastVaccination
            : undefined,
          recommendedIntervalDays: BOOSTER_INTERVAL_DAYS,
          lastVaccinationDate: mostRecentPneumococcal?.date,
        },
      })
    }
  }

  return services
}

/**
 * HSE / NIAC: HPV vaccine (e.g. HPV9) in school programme; catch-up for young adults.
 */
function calculateHpvImmunisationServices(
  patient: Patient,
  age: number,
  immunisations: Immunisation[],
): PreventiveService[] {
  const inSchoolCohort = age >= 12 && age <= 17
  const femaleCatchUp = patient.gender === 'female' && age >= 18 && age <= 25
  if (!inSchoolCohort && !femaleCatchUp) {
    return []
  }

  const HPV_MARKERS = ['hpv', 'hpv9', 'gardasil', 'cervarix', '9-valent']
  const hasRecordedHpv = immunisations.some((imm) => {
    const t = imm.type.toLowerCase()
    return HPV_MARKERS.some((marker) => t.includes(marker))
  })
  if (hasRecordedHpv) {
    return []
  }

  const riskFactors: RiskFactor[] = [
    {
      type: 'age',
      value: `${age}`,
      description: inSchoolCohort
        ? 'Adolescent (school immunisation programme age range)'
        : 'Eligible for catch-up vaccination',
    },
  ]
  if (femaleCatchUp) {
    riskFactors.push({
      type: 'gender',
      value: 'female',
      description: 'Female — extended catch-up cohort',
    })
  }

  const name = 'HPV immunisation series'
  const description = inSchoolCohort
    ? 'HPV vaccine is offered in the second-level school programme; record doses given or arrange catch-up per NIAC / HSE guidance.'
    : 'HPV catch-up may be appropriate for women in this age range who have not completed the series; confirm eligibility per NIAC.'

  return [
    {
      id: `hpv-${patient.id}`,
      type: 'immunization',
      name,
      description,
      status: 'due',
      priority: inSchoolCohort ? 'high' : 'medium',
      reason: inSchoolCohort
        ? 'School programme age group — HPV series not recorded'
        : 'Female catch-up cohort — HPV series not recorded',
      riskFactors,
      actionUrl: `#immunisations`,
      actionLabel: 'Record Immunization',
      metadata: {
        vaccineType: 'hpv',
        guidelineReference: 'NIAC / HSE immunisation programme',
      },
    },
  ]
}

/**
 * National cervical screening programme (CervicalCheck): ages 25–65 for people with a cervix — criteria applied to recorded female gender.
 */
function calculateCervicalScreeningServices(
  patient: Patient,
  age: number,
  currentDate: Date
): PreventiveService[] {
  if (patient.gender !== 'female' || age < 25 || age > 65) {
    return []
  }

  const SCREEN_INTERVAL_DAYS = 3 * 365
  const lastRaw = patient.lastCervicalScreeningDate
  const last = lastRaw ? parseIsoDateLocal(lastRaw) : null

  if (!last) {
    return [
      {
        id: `cervical-screening-${patient.id}`,
        type: 'screening',
        name: 'Cervical screening (CervicalCheck)',
        description:
          'Patients aged 25–65 should participate in the national cervical screening programme. Record last test date or arrange follow-up per CervicalCheck / programme invitations.',
        status: 'due',
        priority: 'medium',
        reason:
          'Ages 25–65 — verify screening is up to date (last screening date not recorded)',
        riskFactors: [
          {
            type: 'gender',
            value: 'female',
            description: 'Eligible population per national programme (recorded gender female)',
          },
          { type: 'age', value: `${age}`, description: 'Within programme age range' },
        ],
        actionUrl: `/patients/${patient.id}/edit#clinical-and-lifestyle`,
        actionLabel: 'Update patient record',
        metadata: {
          screeningType: 'cervical',
          guidelineReference: 'HSE CervicalCheck',
        },
      },
    ]
  }

  const daysSince = Math.floor(
    (currentDate.getTime() - last.getTime()) / (1000 * 60 * 60 * 24)
  )
  if (daysSince < SCREEN_INTERVAL_DAYS) {
    return []
  }

  const dueDate = new Date(last.getTime() + SCREEN_INTERVAL_DAYS * 24 * 60 * 60 * 1000)
  const status: PreventiveServiceStatus =
    dueDate < currentDate ? 'overdue' : 'due'

  return [
    {
      id: `cervical-screening-${patient.id}`,
      type: 'screening',
      name: 'Cervical screening (CervicalCheck)',
      description:
        'Repeat cervical screening is typically due approximately every three years after a negative round (confirm interval with current national programme rules and test history).',
      status,
      priority: status === 'overdue' ? 'high' : 'medium',
      reason: `Last recorded screening ${daysSince} days ago (review due per programme interval)`,
      riskFactors: [
        {
          type: 'gender',
          value: 'female',
          description: 'Eligible population per national programme (recorded gender female)',
        },
      ],
      dueDate: dueDate.toISOString().split('T')[0],
      overdueDate: status === 'overdue' ? dueDate.toISOString().split('T')[0] : undefined,
      actionUrl: `/patients/${patient.id}/edit#clinical-and-lifestyle`,
      actionLabel: 'Update patient record',
      metadata: {
        screeningType: 'cervical',
        guidelineReference: 'HSE CervicalCheck',
        daysSinceLastScreening: daysSince,
      },
    },
  ]
}

/**
 * Influenza: annual vaccine; higher priority for age 65+, chronic disease, pregnancy.
 */
function calculateInfluenzaVaccineServices(
  patient: Patient,
  age: number | null,
  chronicConditions: string[],
  immunisations: Immunisation[],
  currentDate: Date,
  hasActivePregnancy: boolean
): PreventiveService[] {
  if (age === null && !hasActivePregnancy) {
    return []
  }

  const services: PreventiveService[] = []
  const INFLUENZA_VACCINE_TYPES = [
    'influenza',
    'flu',
    'seasonal flu',
    'flu shot',
    'influenza vaccine',
    'seasonal influenza',
    'flu jab',
  ]
  const ANNUAL_INTERVAL_DAYS = 365

  const influenzaImmunisations = immunisations.filter((imm) =>
    INFLUENZA_VACCINE_TYPES.some((type) =>
      imm.type.toLowerCase().includes(type.toLowerCase())
    )
  )

  const mostRecentInfluenza =
    influenzaImmunisations.length > 0
      ? influenzaImmunisations.reduce((latest, current) => {
          return new Date(current.date) > new Date(latest.date) ? current : latest
        })
      : null

  const daysSinceLastVaccination = mostRecentInfluenza
    ? Math.floor(
        (currentDate.getTime() - new Date(mostRecentInfluenza.date).getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : Infinity

  const riskFactors: RiskFactor[] = []
  const reasonParts: string[] = []
  let priority: PreventiveServicePriority = 'medium'

  if (hasActivePregnancy) {
    riskFactors.push({
      type: 'pregnancy',
      value: 'active',
      description: 'Active pregnancy (influenza recommended for all trimesters per NIAC)',
    })
    reasonParts.push('Pregnancy')
    priority = 'high'
  }

  if (age !== null && age >= 65) {
    riskFactors.push({ type: 'age', value: '65+', description: 'Age 65 years or older' })
    reasonParts.push('Age 65+')
    priority = 'high'
    if (process.env.NODE_ENV === 'development') {
      console.log('Influenza recommendation for age 65+:', {
        patientId: patient.id,
        dateOfBirth: patient.dateOfBirth,
        calculatedAge: age,
      })
    }
  } else if (age !== null && age < 65 && chronicConditions && chronicConditions.length > 0) {
    riskFactors.push({
      type: 'chronic-condition',
      value: 'chronic-conditions',
      description: 'Chronic conditions that increase risk',
    })
    reasonParts.push('Chronic conditions')
    priority = 'high'
  }

  if (age !== null && reasonParts.length === 0 && !hasActivePregnancy) {
    reasonParts.push('Annual influenza vaccination recommended')
    priority = 'medium'
  }

  if (reasonParts.length === 0) {
    reasonParts.push('Influenza vaccine due')
  }

  const reason = reasonParts.join('; ')

  const hasRecentVaccination =
    mostRecentInfluenza && daysSinceLastVaccination < ANNUAL_INTERVAL_DAYS
  const isDue = !hasRecentVaccination

  if (isDue) {
    const dueDate = mostRecentInfluenza
      ? new Date(
          new Date(mostRecentInfluenza.date).getTime() +
            ANNUAL_INTERVAL_DAYS * 24 * 60 * 60 * 1000
        )
      : currentDate

    const status: PreventiveServiceStatus =
      dueDate < currentDate && !mostRecentInfluenza ? 'overdue' : 'due'

    services.push({
      id: `influenza-${patient.id}`,
      type: 'immunization',
      name: 'Influenza Vaccine',
      description:
        'Annual influenza vaccination is recommended for everyone; especially important for people aged 65+, with chronic conditions, and during pregnancy.',
      status,
      priority,
      dueDate: dueDate.toISOString().split('T')[0],
      overdueDate:
        status === 'overdue' ? dueDate.toISOString().split('T')[0] : undefined,
      reason,
      riskFactors,
      actionUrl: `#immunisations`,
      actionLabel: 'Record Immunization',
      metadata: {
        vaccineType: 'influenza',
        daysSinceLastVaccination: mostRecentInfluenza
          ? daysSinceLastVaccination
          : undefined,
        recommendedIntervalDays: ANNUAL_INTERVAL_DAYS,
        lastVaccinationDate: mostRecentInfluenza?.date,
      },
    })
  }

  return services
}

function hasHighRiskChronicConditions(chronicConditions: string[]): boolean {
  if (!chronicConditions || chronicConditions.length === 0) {
    return false
  }

  const highRiskConditions = [
    'diabetes',
    'heart disease',
    'heart failure',
    'coronary',
    'lung disease',
    'COPD',
    'asthma',
    'chronic obstructive',
    'kidney disease',
    'renal',
    'liver disease',
    'hepatic',
    'immunosuppression',
    'immunosuppressed',
    'HIV',
    'cancer',
    'splenectomy',
  ]

  const conditionsLower = chronicConditions.map((c) => c.toLowerCase())

  return highRiskConditions.some((risk) =>
    conditionsLower.some((condition) => condition.includes(risk))
  )
}

function formatRelativeDate(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffMonths = Math.floor(diffDays / 30)
  const diffYears = Math.floor(diffDays / 365)

  if (diffYears > 0) {
    return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`
  } else if (diffMonths > 0) {
    return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`
  } else {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  }
}
